import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  UserCheck, UserX, HeartPulse, ShieldBan, Trophy, Target, Hand,
  Award, BookOpen, Shirt, Star, AlertTriangle
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AVAILABILITY_CARDS = [
  { key: 'available', label: 'Available', icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { key: 'unavailable', label: 'Unavailable', icon: UserX, color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' },
  { key: 'injured', label: 'Injured', icon: HeartPulse, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  { key: 'suspended', label: 'Suspended', icon: ShieldBan, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
];

const LEADERBOARD_CONFIG = [
  { key: 'goals', label: 'Top Scorers', icon: Target, unit: '' },
  { key: 'assists', label: 'Most Assists', icon: Hand, unit: '' },
  { key: 'player_of_match', label: 'Man of the Match', icon: Award, unit: '' },
  { key: 'squad_selections', label: 'Most Appearances', icon: Shirt, unit: '' },
  { key: 'training_attendance', label: 'Training Attendance', icon: BookOpen, unit: '%' },
  { key: 'yellow_cards', label: 'Yellow Cards', icon: AlertTriangle, unit: '' },
  { key: 'red_cards', label: 'Red Cards', icon: ShieldBan, unit: '' },
];

function LeaderboardCard({ config, data }) {
  const Icon = config.icon;
  const entries = data || [];
  if (entries.length === 0) {
    return (
      <Card className="border border-gray-100" data-testid={`leaderboard-${config.key}`}>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
            <Icon className="w-4 h-4 text-emerald-600" />
            {config.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <p className="text-xs text-gray-400 text-center py-4">No data yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-100" data-testid={`leaderboard-${config.key}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
          <Icon className="w-4 h-4 text-emerald-600" />
          {config.label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-2">
          {entries.map((entry, i) => (
            <div key={entry.player_id} className="flex items-center gap-2" data-testid={`leader-${config.key}-${i}`}>
              <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
                i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {i + 1}
              </span>
              {entry.photo ? (
                <img src={entry.photo.startsWith('/api') ? `${API}${entry.photo}` : entry.photo}
                  alt={entry.name} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-[10px] font-bold text-emerald-700">
                  {entry.jersey_number || entry.name?.charAt(0)}
                </div>
              )}
              <span className="flex-1 text-sm truncate">{entry.name}</span>
              <span className="text-sm font-semibold tabular-nums">{entry.value}{config.unit}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TopPerformerCard({ performer }) {
  if (!performer) {
    return (
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="top-performer">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Top Performer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400 text-center py-6">Play some matches to see your top performer</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="top-performer">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          Top Performer
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          {performer.photo ? (
            <img src={performer.photo.startsWith('/api') ? `${API}${performer.photo}` : performer.photo}
              alt={performer.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-amber-300" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-lg font-bold text-amber-700 ring-2 ring-amber-300">
              {performer.jersey_number || performer.name?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{performer.name}</p>
            <p className="text-xs text-gray-500">{performer.position}{performer.jersey_number ? ` | #${performer.jersey_number}` : ''}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4">
          {[
            { label: 'Goals', value: performer.goals },
            { label: 'Assists', value: performer.assists },
            { label: 'MOTM', value: performer.motm },
            { label: 'Apps', value: performer.matches },
            { label: 'Training', value: `${performer.training_rate}%` },
          ].map(s => (
            <div key={s.label} className="text-center bg-white rounded-lg py-2 px-1 border border-gray-100">
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DressingRoom() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/dressing-room/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch dressing room summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const avail = data?.availability || {};
  const boards = data?.leaderboards || {};

  return (
    <Layout>
      {/* Header Image - Full width, flush to top (matches Clubhouse) */}
      {user?.dressing_room_header_image && (
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 mb-6 overflow-hidden relative">
          <img
            src={user.dressing_room_header_image.startsWith('/api') ? `${API}${user.dressing_room_header_image}` : user.dressing_room_header_image}
            alt="Dressing Room Header"
            className="w-full h-48 sm:h-60 md:h-72 lg:h-80 object-cover object-top"
            data-testid="dressing-room-header-image"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.2) 70%, transparent 100%)'
            }}
          />
        </div>
      )}

      <div className="space-y-6 page-slide-in" data-testid="dressing-room-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dressing Room</h1>
          <p className="text-sm text-gray-500 mt-1">Squad overview, availability and player stats</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Availability Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="availability-summary">
              {AVAILABILITY_CARDS.map(card => {
                const Icon = card.icon;
                const count = avail[card.key] || 0;
                return (
                  <Card key={card.key} className={`${card.bg} ${card.border} border`} data-testid={`avail-${card.key}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center ${card.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-gray-500">{card.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Team Stats Bar */}
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-emerald-600" />
                {avail.total || 0} Players
              </Badge>
              <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5">
                <Shirt className="w-3.5 h-3.5 text-blue-600" />
                {boards.matches_played || 0} Matches
              </Badge>
              <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5">
                <ShieldBan className="w-3.5 h-3.5 text-emerald-600" />
                {boards.clean_sheets || 0} Clean Sheets
              </Badge>
            </div>

            {/* Top Performer */}
            <TopPerformerCard performer={data?.top_performer} />

            {/* Stat Leaderboards */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Player Leaderboards</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {LEADERBOARD_CONFIG.map(config => (
                  <LeaderboardCard key={config.key} config={config} data={boards[config.key]} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
